/* ══════════════════════════════════════════════════════════════
   Options Chain — Full CE/PE OI, IV, Greeks, Max Pain Table
   ══════════════════════════════════════════════════════════════ */

import { apiFetch } from '../services/api-client';
import { renderUnavailableBlock } from './data-state';

export interface OptionStrike {
  strike: number;
  ceOI: number; ceLTP: number; ceIV: number; ceChange: number;
  peOI: number; peLTP: number; peIV: number; peChange: number;
}

export interface OptionsChainData {
  symbol: string;
  expiry: string;
  spotPrice: number;
  maxPain: number;
  pcr: number;
  strikes: OptionStrike[];
}

function mapNseChain(result: {
  symbol: string;
  expiryDates?: string[];
  underlyingValue: number;
  maxPainStrike: number;
  pcr: number;
  data: Array<{
    strikePrice: number;
    ce?: { openInterest: number; lastPrice: number; impliedVolatility: number; changeinOpenInterest: number };
    pe?: { openInterest: number; lastPrice: number; impliedVolatility: number; changeinOpenInterest: number };
  }>;
}): OptionsChainData {
  const strikes = result.data.map(row => ({
    strike: row.strikePrice,
    ceOI: row.ce?.openInterest || 0,
    ceLTP: row.ce?.lastPrice || 0,
    ceIV: row.ce?.impliedVolatility || 0,
    ceChange: row.ce?.changeinOpenInterest || 0,
    peOI: row.pe?.openInterest || 0,
    peLTP: row.pe?.lastPrice || 0,
    peIV: row.pe?.impliedVolatility || 0,
    peChange: row.pe?.changeinOpenInterest || 0,
  }));

  return {
    symbol: result.symbol,
    expiry: result.expiryDates?.[0] || '',
    spotPrice: result.underlyingValue,
    maxPain: result.maxPainStrike,
    pcr: result.pcr,
    strikes,
  };
}

function renderWithData(container: HTMLElement, d: OptionsChainData): void {
  const fmt = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(0)}K` : String(n);
  const isATM = (s: number) => Math.abs(s - d.spotPrice) < 50;

  const rows = d.strikes.map(s => {
    const atm = isATM(s.strike) ? 'atm-row' : '';
    const itm_ce = s.strike < d.spotPrice ? 'itm' : '';
    const itm_pe = s.strike > d.spotPrice ? 'itm' : '';
    return `<tr class="${atm}">
      <td class="oi-cell ${itm_ce}">${fmt(s.ceOI)}</td>
      <td class="${s.ceChange >= 0 ? 'up' : 'down'}">${s.ceChange >= 0 ? '+' : ''}${s.ceChange}</td>
      <td>${s.ceLTP.toFixed(1)}</td>
      <td>${s.ceIV.toFixed(1)}</td>
      <td class="strike-cell"><strong>${s.strike.toLocaleString('en-IN')}</strong></td>
      <td>${s.peIV.toFixed(1)}</td>
      <td>${s.peLTP.toFixed(1)}</td>
      <td class="${s.peChange >= 0 ? 'up' : 'down'}">${s.peChange >= 0 ? '+' : ''}${s.peChange}</td>
      <td class="oi-cell ${itm_pe}">${fmt(s.peOI)}</td>
    </tr>`;
  }).join('');

  container.innerHTML = `
    <div class="options-chain glass-card">
      <div class="card-header">
        <h3>📋 ${d.symbol} Options Chain</h3>
        <div class="oc-meta">
          <span>Expiry: ${d.expiry}</span>
          <span>Spot: ₹${d.spotPrice.toLocaleString('en-IN')}</span>
          <span>Max Pain: ₹${d.maxPain.toLocaleString('en-IN')}</span>
          <span>PCR: ${d.pcr.toFixed(2)}</span>
        </div>
      </div>
      <div class="table-responsive">
        <table class="oc-table">
          <thead>
            <tr>
              <th colspan="4" class="ce-header">CALLS (CE)</th>
              <th>Strike</th>
              <th colspan="4" class="pe-header">PUTS (PE)</th>
            </tr>
            <tr><th>OI</th><th>Chg</th><th>LTP</th><th>IV</th><th></th><th>IV</th><th>LTP</th><th>Chg</th><th>OI</th></tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </div>`;
}

export function renderOptionsChain(container: HTMLElement, data?: OptionsChainData): void {
  if (data) {
    renderWithData(container, data);
  } else {
    container.innerHTML = `<div class="options-chain glass-card" style="padding:2rem;text-align:center;">Loading options chain...</div>`;

    // Try Upstox options first (real-time with Greeks/OI/IV)
    apiFetch<{
      symbol: string; expiry: string; spotPrice: number; maxPain: number; pcr: number;
      strikes: Array<{
        strike: number; ceOI: number; ceLTP: number; ceIV: number; ceChange: number;
        peOI: number; peLTP: number; peIV: number; peChange: number;
      }>;
    }>('/upstox-options', { cacheTTL: 10 }).then(result => {
      if (result.data && result.data.strikes?.length > 0) {
        renderWithData(container, result.data);
      } else {
        throw new Error('No Upstox options data');
      }
    }).catch(() => {
      // Fallback to NSE options
      apiFetch<{
        symbol: string;
        expiryDates?: string[];
        underlyingValue: number;
        maxPainStrike: number;
        pcr: number;
        data: Array<{
          strikePrice: number;
          ce?: { openInterest: number; lastPrice: number; impliedVolatility: number; changeinOpenInterest: number };
          pe?: { openInterest: number; lastPrice: number; impliedVolatility: number; changeinOpenInterest: number };
        }>;
      }>('/nse-options', { cacheTTL: 30 }).then(result => {
        if (result.data && result.data.data?.length > 0) {
          renderWithData(container, mapNseChain(result.data));
        } else {
          container.innerHTML = renderUnavailableBlock('options-chain glass-card', 'Live options chain unavailable');
        }
      }).catch(() => {
        container.innerHTML = renderUnavailableBlock('options-chain glass-card', 'Live options chain unavailable');
      });
    });
  }
}
